<template>
  <!-- Converter Box -->
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative">
    <!-- Hidden File Input (Moved outside to ensure it's always accessible) -->
    <input type="file" ref="fileInput" class="hidden" multiple accept="image/*,.heic,.heif,.tiff,.tif,.dds" @change="handleFileSelect" />

    <!-- Dropzone -->
    <div v-if="files.length === 0" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" class="p-10 md:p-6">
      <div :class="['border-2 border-dashed rounded-xl p-8 text-center transition-colors', isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50']">
        <div @click="triggerFileInput" class="cursor-pointer">
          <div class="w-16 h-16 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center mx-auto mb-2">
            <UploadCloud class="w-8 h-8 text-blue-600" />
          </div>
          <span class="text-xl font-semibold text-slate-900 mb-2 block">Drop your images here</span>
          <p class="text-gray-500 text-xs font-medium mb-1 mt-1">PNG, JPG, WebP, HEIC, GIF, SVG up to 50MB</p>
          <span class="text-slate-400 text-xs font-semibold block mb-5"><span class="bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded font-mono font-bold text-[10px] text-slate-700">CTRL + V</span> to paste from clipboard</span>
        </div>
        
        <div class="flex items-center justify-center gap-3">
          <button @click="triggerFileInput" class="inline-flex px-3 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm shadow-sm hover:bg-blue-700 transition-colors cursor-pointer">
            <Plus class="w-4 h-4 text-white mr-1 ml-[-5px] mt-[3px]" /> Select Files
          </button>
          <button @click="openUrlModal" class="inline-flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 text-blue-600 hover:border-blue-400 hover:text-white hover:bg-blue-600 rounded-lg font-bold text-sm shadow-sm transition-all cursor-pointer">
            <Link class="w-4 h-4" />
          </button>
        </div>
      </div>
          <!-- Privacy Shield -->
      <div class="mt-5 ml-3 bg-gray-50/30 border-t border-gray-50 flex items-center justify-start gap-2">
        <ShieldCheck class="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
        <p class="text-[9px] md:text-[13px] font-semibold text-blue-600">
          *Your privacy is protected! No data is transmitted or stored.
        </p>
      </div>
    </div>

    <!-- File List -->
    <div v-else class="p-4 md:p-4">
      <div class="flex items-center justify-between mb-1 px-4">
          <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <span class="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">{{ files.length }}</span>
               <p class="text-sm font-bold text-slate-900 uppercase tracking-tight">Files Queue</p>
                <span class="text-[11px] font-semibold text-slate-400">• {{ totalQueueSize }}</span>
              </div>
          </div>
      </div>

      <div ref="fileListContainer" @scroll="handleListScroll" class="space-y-1.5 max-h-[459px] overflow-y-auto p-3 px-4 rounded-2xl custom-scrollbar relative">
        <div v-for="(file, index) in files" :key="index" class="bg-white rounded-xl p-1.5 px-2.5 md:p-2 md:px-4 flex flex-row items-center justify-between gap-2 md:gap-3 border border-gray-200 hover:shadow-sm transition-all group/item">
        <div class="flex items-center gap-2 md:gap-3 flex-1 min-w-0 overflow-hidden">
          <!-- Thumbnail/Preview -->
          <div @click="openPreview(file.preview)" class="w-16 h-16 md:w-14 md:h-12 rounded-md md:rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center p-1 cursor-pointer group/thumb relative">
            <template v-if="file.isGeneratingPreview">
              <!-- Loading preview spinner -->
              <div class="flex flex-col items-center justify-center gap-0.5">
                <Loader2 class="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 animate-spin" />
                <span class="hidden md:inline-block text-[8px] font-semibold text-blue-500 uppercase">Decoding</span>
              </div>
            </template>
            <template v-else-if="file.preview && !file.previewError">
              <img :src="file.preview" class="w-full h-full object-contain transition-transform duration-300" @error="file.previewError = true" />
              <div class="absolute inset-0 bg-black/20 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                <Eye class="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
            </template>
            <template v-else>
              <!-- Beautiful custom icon based on file format -->
              <div class="w-full h-full rounded-md flex flex-col items-center justify-center relative overflow-hidden" :class="getFormatIconClass(getExt(file.name))">
                <span class="text-[8px] md:text-[10px] font-extrabold tracking-wider uppercase z-10">{{ getExt(file.name) }}</span>
                <!-- Background visual element to look premium -->
                <div class="absolute inset-0 opacity-10 bg-current"></div>
              </div>
            </template>
          </div>
          <div class="min-w-0 flex-1">
            <h4 class="font-bold text-slate-800 truncate text-[11px] md:text-xs mb-0.5 max-w-[80px] sm:max-w-[150px] md:max-w-[320px]" :title="file.name">{{ file.name }}</h4>
            <div class="flex items-center gap-1 md:gap-2">
              <span class="text-[9px] md:text-[10px] text-blue-600 font-bold uppercase">{{ formatSize(file.size) }}</span>
              <span class="text-[9px] md:text-[10px] text-slate-300 font-semibold">•</span>
              <span class="text-[9px] md:text-[10px] text-slate-400 font-semibold uppercase">{{ getExt(file.name) }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
          <div class="flex items-center gap-1 md:gap-2.5">
            <span class="hidden sm:inline-block text-[12px] font-semibold text-slate-600">Convert To</span>
            
            <!-- Custom Format Selector -->
            <div class="relative min-w-[75px] md:min-w-[90px]">
              <button @click.stop="toggleDropdown(index, $event)" :class="['w-full border rounded-md px-2 py-1.5 md:px-4 md:py-2 text-[10px] md:text-xs font-bold flex items-center justify-between transition-all outline-none shadow-sm', file.targetFormat ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' : 'bg-white border-gray-200 text-slate-600 hover:border-blue-500 hover:text-blue-600']">
                <span class="tracking-tight">{{ file.targetFormat || 'Select' }}</span>
                <ChevronDown class="w-3 h-3 md:w-4 md:h-4 opacity-70 ml-1 flex-shrink-0" :class="{ 'rotate-180': activeDropdown === index }"/>
              </button>

              <!-- Dropdown Menu -->
              <teleport to="body">
                <transition name="dropdown">
                    <div v-if="activeDropdown === index" :style="dropdownStyle" :class="['bg-white border border-blue-100 rounded-md shadow-2xl z-[9999] p-2.5 grid grid-cols-3 gap-2 ring-1 ring-blue-50', dropdownIsUpward ? 'origin-bottom' : 'origin-top']">
                    <button v-for="format in getFilteredFormats(getExt(file.name))" :key="format" @click="selectFormat(file, format)" class="text-center px-1 py-3 text-[11px] font-bold rounded-md bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all border border-blue-100 hover:border-blue-400 shadow-sm uppercase tracking-tight">
                        {{ format }}
                    </button>
                    </div>
                </transition>
              </teleport>
            </div>
          </div>
          
          <div class="flex items-center gap-1 md:gap-2">
            <!-- Individual Action -->
            <div class="flex items-center">
              <!-- Loading Spinner -->
              <div v-if="file.isConverting" class="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center">
                <svg class="animate-spin h-4 w-4 md:h-5 md:w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>

              <!-- Convert Button -->
              <button v-else-if="!file.convertedUrl" 
                @click="individualConvert(file)" 
                @mouseenter="showTooltip($event, file.targetFormat ? 'Convert Now' : 'Select Format First')" 
                @mouseleave="hideTooltip"
                :disabled="!file.targetFormat" 
                :class="['w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center transition-all group/btn shadow-sm', file.targetFormat ? 'bg-slate-900 text-white hover:bg-black hover:shadow-md cursor-pointer' : 'bg-slate-900 text-white cursor-default opacity-30']"
              >
                <RefreshCw class="w-3 h-3 md:w-3.5 md:h-3.5 transition-transform duration-500" :class="{ 'group-hover/btn:rotate-180': file.targetFormat }" />
              </button>

              <!-- Download Button -->
              <a v-else-if="file.convertedUrl" 
                :href="file.convertedUrl" 
                :download="file.downloadName" 
                @mouseenter="showTooltip($event, 'Download')" 
                @mouseleave="hideTooltip"
                class="w-7 h-7 md:w-8 md:h-8 rounded-md bg-green-600 flex items-center justify-center text-white transition-all hover:bg-green-700 hover:shadow-md shadow-sm"
              >
                <Download class="w-3 h-3 md:w-3.5 md:h-3.5" />
              </a>
            </div>

            <!-- Remove Button -->
            <button @click="removeFile(index)" 
              @mouseenter="showTooltip($event, 'Remove')" 
              @mouseleave="hideTooltip"
              class="w-7 h-7 md:w-8 md:h-8 rounded-md bg-red-500 flex items-center justify-center text-white transition-all hover:bg-red-600 hover:shadow-md shadow-sm"
            >
              <Trash2 class="w-3 h-3 md:w-3.5 md:h-3.5" />
            </button>
          </div>
        </div>
      </div>
      </div>
    
      <!-- Bulk Actions -->
      <div v-if="files.length > 0" class="mt-2 bg-gray-50/50 rounded-md p-3 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-gray-100">
        <div class="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 w-full md:w-auto">
            <div class="flex flex-wrap items-center justify-center md:justify-start gap-2 w-full md:w-auto">
                <button @click="openUrlModal" class="bg-blue-50 border border-blue-100 hover:border-blue-200 hover:bg-blue-100 px-3 py-2.5 rounded-md text-xs font-bold text-blue-600 transition-all flex items-center gap-2 shadow-sm">
                    <Link class="w-4 h-4" />
                </button>
                <button @click="triggerFileInput" class="bg-blue-50 border border-blue-100 hover:border-blue-200 hover:bg-blue-100 px-3.5 py-2 rounded-md text-xs font-bold text-blue-600 transition-all flex items-center gap-2 shadow-sm">
                    <Plus class="w-4 h-4" />
                    Add More
                </button>
                <button @click="clearAll" class="bg-red-50 border border-red-100 hover:border-red-200 hover:bg-red-100 px-3.5 py-2 rounded-md text-xs font-bold text-red-600 transition-all flex items-center gap-2 shadow-sm">
                    <RotateCcw class="w-3.5 h-3.5" />
                    Start Over
                </button>
            </div>
            
            <div v-if="files.length > 1" class="h-8 w-px bg-gray-200 hidden md:block"></div>

            <div v-if="files.length > 1" class="flex items-center justify-center md:justify-start gap-3 w-full md:w-auto">
                <p class="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Convert All To</p>
                <div class="relative min-w-[110px]">
                    <button @click.stop="toggleBulkDropdown($event)" :class="['w-full border rounded-md px-4 py-2 text-xs font-bold flex items-center justify-between transition-all outline-none', bulkFormat ? 'bg-blue-50 border-blue-200 text-blue-700 hover:border-blue-100' : 'bg-white border-gray-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 shadow-sm']">
                        <span class="tracking-tight">{{ bulkFormat || 'Select' }}</span>
                        <ChevronDown class="w-3.5 h-3.5 opacity-70 transition-transform duration-200" :class="{ 'rotate-180': isBulkDropdownOpen }"/>
                    </button>

                    <!-- Dropdown Menu -->
                    <teleport to="body">
                        <transition name="dropdown">
                            <div v-if="isBulkDropdownOpen" :style="dropdownStyle" class="bg-white border border-blue-100 rounded-md shadow-2xl z-[9999] p-2.5 grid grid-cols-3 gap-2 ring-1 ring-blue-50">
                                <button v-for="format in commonFormats" :key="format" @click="selectBulkFormat(format)" class="text-center px-1 py-3 text-[11px] font-bold rounded-md bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all border border-blue-100 hover:border-blue-400 shadow-sm uppercase tracking-tight">
                                    {{ format }}
                                </button>
                            </div>
                        </transition>
                    </teleport>
                </div>
            </div>
        </div>
        <button v-if="!isBatchConverted" @click="processFiles" :disabled="!isReadyToConvert" :class="['px-4 py-2.5 rounded-md font-semibold text-sm transition-all flex items-center gap-3 w-full md:w-auto justify-center group shadow-lg', isReadyToConvert ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20' : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none']">
          <span>Convert All Files</span>
          <RefreshCw class="w-4 h-4 transition-transform duration-500" :class="{ 'group-hover:rotate-180': isReadyToConvert }" />
        </button>
        <button v-else @click="handleFinalDownload" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-md font-semibold text-sm transition-all shadow-lg shadow-green-500/20 flex items-center gap-3 w-full md:w-auto justify-center">
          <span>{{ results.length > 1 ? 'Download ZIP' : 'Download Image' }}</span>
          <Download class="w-4 h-4" />
        </button>
      </div>
    </div>
    <!-- Processing Overlay -->
    <div v-if="isProcessing" class="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
        <div class="max-w-xs w-full text-center">
            <Loader2 class="w-12 h-12 text-blue-500 animate-spin mx-auto mb-6" />
            <h3 class="text-xl font-bold text-white mb-2 uppercase tracking-tight">Processing...</h3>
            <div class="w-full bg-white/10 h-1.5 rounded-full overflow-hidden mb-6">
                <div class="bg-blue-500 h-full transition-all duration-300" :style="{ width: progress + '%' }"></div>
            </div>
            <p class="text-slate-400 text-sm font-medium italic">{{ processingMessage }}</p>
        </div>
    </div>


    <!-- Hidden Canvas (No longer needed globally, but kept for script reference if any) -->
    <div class="hidden"></div>
    <!-- Image Preview Modal -->
    <teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="isPreviewOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-8">
          <!-- Backdrop -->
          <div class="absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity duration-300" @click="closePreview"></div>
          
          <!-- Content Container -->
          <div class="relative max-w-5xl w-full max-h-full flex flex-col items-center justify-center transform transition-all duration-300 ease-out scale-100">

            <!-- Image Wrapper -->
            <div class="bg-white/5 p-2 rounded-2xl border border-white/10 shadow-2xl relative overflow-hidden group/modal">
              <!-- Close Button Inside -->
              <button @click="closePreview" class="absolute top-4 right-4 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-md border border-white/10 z-[10002]" title="Close Preview">
                <X class="w-5 h-5" />
              </button>

              <img :src="previewImageSrc" class="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg shadow-inner shadow-black/20" alt="Preview"/>

              <!-- Subtle Info Overlay -->
              <div class="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full opacity-0 group-hover/modal:opacity-100 transition-opacity duration-300 text-white text-xs font-medium">
                Click background to close
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Global Drag and Drop Overlay (Decent & Minimal) -->
    <teleport to="body">
      <transition 
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-[0.98]"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-[0.98]"
      >
        <div v-show="isGlobalDragging" class="fixed inset-0 z-[100005] pointer-events-none p-4 md:p-8 flex items-center justify-center">
            <!-- Subtle Tint -->
            <div class="absolute inset-0 bg-blue-600/[0.02] backdrop-blur-[1px]"></div>
            
            <!-- Viewport Border -->
            <div class="absolute inset-4 md:inset-8 border-2 border-dashed border-blue-400/20 rounded-[2rem]"></div>
            
            <!-- Minimal Floating Pill -->
            <div class="relative bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-blue-100/50 px-6 py-3 rounded-full flex items-center gap-3 animate-pulse">
                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
                    <UploadCloud class="w-4 h-4" />
                </div>
                <span class="text-slate-800 font-bold text-sm tracking-tight">Drop images to upload</span>
            </div>
        </div>
      </transition>
    </teleport>

    <!-- Professional Global Teleported Tooltip -->
    <teleport to="body">
      <transition 
        enter-active-class="transition duration-400 ease-[cubic-bezier(0.23,1,0.32,1)]"
        enter-from-class="opacity-0 translate-y-1.5 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-show="tooltipState.show" 
          :style="{ left: tooltipState.x + 'px', top: tooltipState.y + 'px' }" 
          class="fixed z-[99999] pointer-events-none -translate-x-1/2 mt-3 transform-gpu will-change-transform"
        >
          <div class="bg-[#111010f2] backdrop-blur-md text-white px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-[0_8px_30px_rgb(0,0,0,0.4)] border border-white/10 whitespace-nowrap relative">
            {{ tooltipState.text }}
            <!-- Arrow on Top -->
            <div class="absolute bottom-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-b-[#111010f2]"></div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { 
  UploadCloud, 
  ChevronDown, 
  RefreshCw, 
  Download, 
  Trash2, 
  Plus, 
  Loader2,
  RotateCcw,
  X,
  Eye,
  ShieldCheck,
  Link
} from 'lucide-vue-next';

const files = ref([]);
const results = ref([]);
const fileListContainer = ref(null);

const totalQueueSize = computed(() => {
  const totalBytes = files.value.reduce((sum, file) => sum + (file.size || 0), 0);
  return formatSize(totalBytes);
});

const isReadyToConvert = computed(() => {
    return files.value.length > 0 && files.value.every(f => f.targetFormat);
});

const isDownloadingZip = ref(false);
const isDragging = ref(false);
const fileInput = ref(null);
const isProcessing = ref(false);
const progress = ref(0);
const processingMessage = ref('Loading files...');
const isBatchConverted = ref(false);
const bulkFormat = ref('');
const activeDropdown = ref(null);
const activeDropdownEl = ref(null);
const isBulkDropdownOpen = ref(false);
const dropdownStyle = ref({});
const dropdownIsUpward = ref(false);
const isGlobalDragging = ref(false);
let dragCounter = 0;

// URL Upload & Clipboard States
const openUrlModal = async () => {
  const url = prompt('Enter Image URL');
  if (!url) return;
  
  isProcessing.value = true;
  progress.value = 0;
  processingMessage.value = 'Fetching image...';
  
  try {
    const trimmedUrl = url.trim();
    
    if (trimmedUrl.startsWith('data:')) {
      const parts = trimmedUrl.split(',');
      if (parts.length < 2) {
        throw new Error('Invalid data URL format.');
      }
      
      const mimeMatch = parts[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : '';
      if (!mime || !mime.startsWith('image/')) {
        throw new Error('The data URL provided does not contain a valid image MIME type.');
      }
      
      const isBase64 = parts[0].includes('base64');
      let blob;
      if (isBase64) {
        const bstr = atob(parts[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        blob = new Blob([u8arr], { type: mime });
      } else {
        const decodedData = decodeURIComponent(parts[1]);
        blob = new Blob([decodedData], { type: mime });
      }
      
      const ext = mime.split('/')[1] || 'png';
      const filename = `image_${Date.now()}.${ext}`;
      
      const file = new File([blob], filename, { type: mime });
      await addFiles([file]);
    } else {
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        throw new Error('Please enter a valid URL starting with http://, https://, or data:');
      }
      
      const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(trimmedUrl)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image. Status: ${response.status}`);
      }
      
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) {
        throw new Error('The URL provided does not point to a valid image file.');
      }
      
      let filename = 'url_image';
      try {
        const urlObject = new URL(trimmedUrl);
        const pathname = urlObject.pathname;
        const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);
        if (lastSegment && lastSegment.includes('.')) {
          filename = lastSegment;
        } else {
          const ext = blob.type.split('/')[1] || 'png';
          filename = `image_${Date.now()}.${ext}`;
        }
      } catch (e) {
        const ext = blob.type.split('/')[1] || 'png';
        filename = `image_${Date.now()}.${ext}`;
      }
      
      const file = new File([blob], filename, { type: blob.type });
      await addFiles([file]);
    }
  } catch (err) {
    console.error('Error fetching image from URL:', err);
    alert(err.message || 'Could not fetch image. Please make sure the URL is a direct link to an image file.');
  } finally {
    isProcessing.value = false;
  }
};

const handlePaste = async (e) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  
  const pastedFiles = [];
  for (const item of items) {
    if (item.type.indexOf('image') !== -1) {
      const file = item.getAsFile();
      if (file) {
        const ext = file.type.split('/')[1] || 'png';
        const fileName = `pasted_image_${Date.now()}.${ext}`;
        const namedFile = new File([file], fileName, { type: file.type });
        pastedFiles.push(namedFile);
      }
    }
  }
  
  if (pastedFiles.length > 0) {
    await addFiles(pastedFiles);
  }
};

// Global Tooltip State
const tooltipState = reactive({
  show: false,
  text: '',
  x: 0,
  y: 0
});

const showTooltip = (e, text) => {
  const rect = e.currentTarget.getBoundingClientRect();
  tooltipState.text = text;
  tooltipState.x = rect.left + rect.width / 2;
  tooltipState.y = rect.bottom;
  tooltipState.show = true;
};

const hideTooltip = () => {
  tooltipState.show = false;
};

const allFormats = ['PNG', 'JPG', 'WEBP', 'PDF', 'AVIF', 'BMP', 'TIFF', 'ICO'];
const commonFormats = ['PNG', 'JPG', 'WEBP', 'PDF', 'AVIF', 'BMP', 'TIFF', 'ICO'];

// Image Preview State
const isPreviewOpen = ref(false);
const previewImageSrc = ref('');

const openPreview = (src) => {
  if (!src) return;
  previewImageSrc.value = src;
  isPreviewOpen.value = true;
};

const closePreview = () => {
  isPreviewOpen.value = false;
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = (e) => {
  addFiles(Array.from(e.target.files));
};

const handleDrop = (e) => {
  isDragging.value = false;
  addFiles(Array.from(e.dataTransfer.files));
};

const getFormatIconClass = (ext) => {
  const format = ext ? ext.toUpperCase() : '';
  switch (format) {
    case 'TIFF':
    case 'TIF':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'DDS':
      return 'bg-purple-50 text-purple-700 border border-purple-200';
    case 'HEIC':
    case 'HEIF':
      return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
    case 'PNG':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'SVG':
      return 'bg-orange-50 text-orange-700 border border-orange-200';
    case 'JPG':
    case 'JPEG':
    case 'JPE':
    case 'JFIF':
      return 'bg-blue-50 text-blue-700 border border-blue-200';
    case 'WEBP':
      return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
    case 'PDF':
      return 'bg-rose-50 text-rose-700 border border-rose-200';
    default:
      return 'bg-gray-50 text-gray-700 border border-gray-200';
  }
};

const generateTiffPreview = async (file) => {
  const buffer = await file.arrayBuffer();
  const UTIF = (await import('utif')).default;
  const ifds = UTIF.decode(buffer);
  if (!ifds || ifds.length === 0) throw new Error('No IFD found');
  const ifd = ifds[0];
  UTIF.decodeImage(buffer, ifd);
  const rgba = UTIF.toRGBA8(ifd);
  const canvas = document.createElement('canvas');
  canvas.width = ifd.width;
  canvas.height = ifd.height;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(ifd.width, ifd.height);
  imgData.data.set(rgba);
  ctx.putImageData(imgData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/jpeg', 0.9);
  });
};

const generateDdsPreview = async (file) => {
  const buffer = await file.arrayBuffer();
  const parseDDS = (await import('parse-dds')).default;
  const decodeDXT = (await import('decode-dxt')).default;
  const ddsData = parseDDS(new Uint8Array(buffer));
  const image = ddsData.images[0];
  const imageWidth = image.shape[0];
  const imageHeight = image.shape[1];
  
  const imageDataView = new DataView(buffer, image.offset, image.length);
  const rgba = decodeDXT(imageDataView, imageWidth, imageHeight, ddsData.format);
  
  const canvas = document.createElement('canvas');
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(imageWidth, imageHeight);
  imgData.data.set(rgba);
  ctx.putImageData(imgData, 0, 0);
  
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/jpeg', 0.9);
  });
};

const generateFilePreview = async (fileItem) => {
  const ext = getExt(fileItem.name).toLowerCase();
  const nativeFormats = ['png', 'jpg', 'jpeg', 'jpe', 'jfif', 'webp', 'gif', 'avif', 'bmp', 'ico', 'svg'];
  
  if (nativeFormats.includes(ext)) {
    try {
      fileItem.preview = URL.createObjectURL(fileItem.file);
    } catch (e) {
      console.error('Error creating native preview:', e);
      fileItem.previewError = true;
    }
    return;
  }
  
  fileItem.isGeneratingPreview = true;
  try {
    let previewBlob = null;
    if (ext === 'heic' || ext === 'heif') {
      const heic2any = (await import('heic2any')).default;
      const converted = await heic2any({ blob: fileItem.file, toType: 'image/jpeg', quality: 0.3 });
      previewBlob = Array.isArray(converted) ? converted[0] : converted;
    } else if (ext === 'tiff' || ext === 'tif') {
      previewBlob = await generateTiffPreview(fileItem.file);
    } else if (ext === 'dds') {
      previewBlob = await generateDdsPreview(fileItem.file);
    }
    
    if (previewBlob) {
      fileItem.preview = URL.createObjectURL(previewBlob);
    } else {
      fileItem.previewError = true;
    }
  } catch (e) {
    console.error(`Error generating preview for ${ext.toUpperCase()}:`, e);
    fileItem.previewError = true;
  } finally {
    fileItem.isGeneratingPreview = false;
  }
};

const addFiles = async (newFiles) => {
  for (const file of newFiles) {
    const ext = getExt(file.name).toLowerCase();
    const isImage = file.type.startsWith('image/');
    const supportedExts = ['png', 'jpg', 'jpeg', 'jpe', 'jfif', 'webp', 'heic', 'heif', 'tiff', 'tif', 'dds', 'gif', 'avif', 'bmp', 'ico', 'svg'];
    const isSupported = isImage || supportedExts.includes(ext);
    
    if (isSupported) {
       const defaultFormat = ext === 'png' ? '' : 'PNG';

       const fileItem = reactive({
         name: file.name,
         size: file.size,
         file: file,
         preview: null,
         previewError: false,
         isGeneratingPreview: false,
         targetFormat: defaultFormat,
         isConverting: false
       });

       files.value.push(fileItem);
       
       // Asynchronously trigger preview generation without blocking the main upload loop
       generateFilePreview(fileItem);
    }
  }

  await nextTick();
  if (fileListContainer.value) {
    fileListContainer.value.scrollTop = fileListContainer.value.scrollHeight;
  }
};

const removeFile = (index) => {
  hideTooltip(); // Fix ghost tooltip
  const file = files.value[index];
  if (file.preview) URL.revokeObjectURL(file.preview);
  files.value.splice(index, 1);
};

const clearAll = () => {
  hideTooltip(); // Fix ghost tooltip
  files.value.forEach(f => {
    if (f.preview) URL.revokeObjectURL(f.preview);
  });
  files.value = [];
  results.value = [];
};

const getExt = (filename) => {
  return filename ? filename.split('.').pop().toUpperCase() : '';
};

// Reset conversion state when files change
watch(files, () => {
    isBatchConverted.value = false;
    results.value = [];
}, { deep: true });

const getFilteredFormats = (sourceExt) => {
  return allFormats;
};

const formatSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const applyBulkFormat = () => {
    if (!bulkFormat.value) return;
    files.value.forEach(file => {
        file.targetFormat = bulkFormat.value;
        file.convertedUrl = null; // Reset generated link
    });
};

const toggleDropdown = (index, event) => {
  if (activeDropdown.value === index) {
    activeDropdown.value = null;
    activeDropdownEl.value = null;
  } else {
    activeDropdown.value = index;
    activeDropdownEl.value = event.currentTarget;
    isBulkDropdownOpen.value = false;
    updateDropdownPosition();
  }
};

const updateDropdownPosition = () => {
    if (activeDropdown.value === null || !activeDropdownEl.value) return;

    const rect = activeDropdownEl.value.getBoundingClientRect();
    const dropdownHeight = 120; // Improved height for grid
    const dropdownWidth = 210;
    const spaceBelow = window.innerHeight - rect.bottom;
    
    dropdownIsUpward.value = spaceBelow < dropdownHeight;

    // Calculate left but keep on screen
    const left = Math.max(16, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 16));

    if (dropdownIsUpward.value) {
        dropdownStyle.value = {
            position: 'fixed',
            bottom: `${window.innerHeight - rect.top + 8}px`,
            left: `${left}px`,
            width: `${dropdownWidth}px`
        };
    } else {
        dropdownStyle.value = {
            position: 'fixed',
            top: `${rect.bottom + 8}px`,
            left: `${left}px`,
            width: `${dropdownWidth}px`
        };
    }
};

const handleListScroll = () => {
    if (activeDropdown.value !== null) {
        updateDropdownPosition();
    }
};

const selectFormat = (file, format) => {
  file.targetFormat = format;
  file.convertedUrl = null; // Reset generated link
  activeDropdown.value = null;
};

const toggleBulkDropdown = (event) => {
  if (isBulkDropdownOpen.value) {
    isBulkDropdownOpen.value = false;
  } else {
    isBulkDropdownOpen.value = true;
    activeDropdown.value = null;

    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth = 210;
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - dropdownWidth - 16));

    dropdownStyle.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`
    };
  }
};

const selectBulkFormat = (format) => {
  bulkFormat.value = format;
  applyBulkFormat();
  isBulkDropdownOpen.value = false;
};

// Close dropdowns on click, scroll, or resize
const closeDropdowns = () => {
  activeDropdown.value = null;
  isBulkDropdownOpen.value = false;
};

const handleGlobalDragEnter = (e) => {
  e.preventDefault();
  dragCounter++;
  if (dragCounter === 1) isGlobalDragging.value = true;
};

const handleGlobalDragLeave = (e) => {
  e.preventDefault();
  dragCounter--;
  if (dragCounter <= 0) {
    dragCounter = 0;
    isGlobalDragging.value = false;
  }
};

const handleGlobalDrop = (e) => {
  e.preventDefault();
  dragCounter = 0;
  isGlobalDragging.value = false;
  if (e.dataTransfer.files) {
    addFiles(Array.from(e.dataTransfer.files));
  }
};

if (typeof window !== 'undefined') {
  onMounted(() => {
    window.addEventListener('dragenter', handleGlobalDragEnter);
    window.addEventListener('dragleave', handleGlobalDragLeave);
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('drop', handleGlobalDrop);
    window.addEventListener('click', closeDropdowns);
    window.addEventListener('scroll', closeDropdowns, { capture: true, passive: true });
    window.addEventListener('resize', closeDropdowns);
    window.addEventListener('paste', handlePaste);
  });

  onUnmounted(() => {
    hideTooltip();
    window.removeEventListener('dragenter', handleGlobalDragEnter);
    window.removeEventListener('dragleave', handleGlobalDragLeave);
    window.removeEventListener('dragover', (e) => e.preventDefault());
    window.removeEventListener('drop', handleGlobalDrop);
    window.removeEventListener('click', closeDropdowns);
    window.removeEventListener('scroll', closeDropdowns, { capture: true });
    window.removeEventListener('resize', closeDropdowns);
    window.removeEventListener('paste', handlePaste);
    clearAll();
  });
}

// Lock scroll when modal is open and prevent layout shift
watch(isPreviewOpen, (newIsPreviewOpen) => {
    if (typeof document !== 'undefined') {
        if (newIsPreviewOpen) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = `${scrollBarWidth}px`;
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.paddingRight = '';
            document.body.style.overflow = '';
        }
    }
});

// Hide/Show Blade hero section based on file count
watch(() => files.value.length, (count) => {
    if (typeof document !== 'undefined') {
        const hero = document.getElementById('hero-section');
        if (hero) {
            hero.style.display = count > 0 ? 'none' : 'block';
        }
    }
}, { immediate: true });

const processFiles = async () => {
    if (files.value.length === 0) return;
    
    isProcessing.value = true;
    progress.value = 0;
    processingMessage.value = 'Preparing files...';
    results.value = [];

    const totalFiles = files.value.length;
    let completedCount = 0;

    try {
        processingMessage.value = 'Converting images...';
        const conversionPromises = files.value.map(async (fileObj) => {
            fileObj.isConverting = true;
            try {
                const result = await convertImage(fileObj);
                fileObj.convertedUrl = result.url;
                fileObj.downloadName = result.downloadName;
                completedCount++;
                progress.value = Math.round((completedCount / totalFiles) * 100);
                return result;
            } finally {
                fileObj.isConverting = false;
            }
        });

        results.value = await Promise.all(conversionPromises);
        isBatchConverted.value = true;
        
        progress.value = 100;
        processingMessage.value = 'Success!';
    } catch (err) {
        console.error('Bulk conversion error:', err);
        processingMessage.value = 'Conversion failed';
    } finally {
        setTimeout(() => {
            isProcessing.value = false;
        }, 800);
    }
};

const handleFinalDownload = async () => {
    if (results.value.length === 0) return;

    if (results.value.length === 1) {
        // Direct download single file
        const res = results.value[0];
        const link = document.createElement('a');
        link.href = res.url;
        link.download = res.downloadName;
        link.click();
    } else {
        // ZIP download multiple files
        await downloadAllAsZip();
    }
};

const individualConvert = async (fileObj) => {
    fileObj.isConverting = true;
    try {
        const result = await convertImage(fileObj);
        fileObj.convertedUrl = result.url;
        fileObj.downloadName = result.downloadName;
    } catch (err) {
        console.error('Individual conversion error:', err);
    } finally {
        fileObj.isConverting = false;
    }
};

const convertAndDownload = async (fileObj) => {
    // This function can be retired or kept for internal use if needed
    await individualConvert(fileObj);
    if (fileObj.convertedUrl) {
        const link = document.createElement('a');
        link.href = fileObj.convertedUrl;
        link.download = fileObj.downloadName;
        link.click();
    }
};

const downloadAllAsZip = async () => {
    if (results.value.length === 0) return;
    
    isDownloadingZip.value = true;
    
    try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const res of results.value) {
            const response = await fetch(res.url);
            const blob = await response.blob();
            zip.file(res.downloadName, blob);
        }
        
        const content = await zip.generateAsync({ type: 'blob' });
        const zipUrl = URL.createObjectURL(content);
        
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `converted_images_${Date.now()}.zip`;
        link.click();
        
        URL.revokeObjectURL(zipUrl);
    } catch (err) {
        console.error('ZIP Error:', err);
    } finally {
        isDownloadingZip.value = false;
    }
};

const convertImage = (fileObj) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = fileObj.preview;
        img.onload = async () => {
            const canvas = document.createElement('canvas'); // Local canvas for speed
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            // Mime types
            const mimes = {
                'PNG': 'image/png',
                'JPG': 'image/jpeg',
                'WEBP': 'image/webp',
                'GIF': 'image/gif',
                'AVIF': 'image/avif',
                'BMP': 'image/bmp',
                'TIFF': 'image/tiff',
                'ICO': 'image/x-icon'
            };

            const targetMime = mimes[fileObj.targetFormat] || 'image/png';
            const quality = fileObj.targetFormat === 'JPG' ? 0.9 : 1;

            if (fileObj.targetFormat === 'PDF') {
                try {
                    const { jsPDF } = await import('jspdf');
                    const pdf = new jsPDF({
                        orientation: img.width > img.height ? 'l' : 'p',
                        unit: 'px',
                        format: [img.width, img.height]
                    });
                    pdf.addImage(canvas.toDataURL('image/jpeg', 1.0), 'JPEG', 0, 0, img.width, img.height);
                    const blob = pdf.output('blob');
                    const url = URL.createObjectURL(blob);
                    const nameWithoutExt = fileObj.name.split('.').slice(0, -1).join('.');
                    
                    resolve({
                        name: fileObj.name,
                        downloadName: `${nameWithoutExt}.pdf`,
                        format: 'PDF',
                        url: url,
                        preview: fileObj.preview
                    });
                } catch (err) {
                    reject(err);
                }
                return;
            }
            
            canvas.toBlob((blob) => {
                if (!blob) return reject('Blob failed');
                const url = URL.createObjectURL(blob);
                const nameWithoutExt = fileObj.name.split('.').slice(0, -1).join('.');
                
                resolve({
                    name: fileObj.name,
                    downloadName: `${nameWithoutExt}.${fileObj.targetFormat.toLowerCase()}`,
                    format: fileObj.targetFormat,
                    url: url,
                    preview: fileObj.preview
                });
            }, targetMime, quality);
        };
        img.onerror = reject;
    });
};

onUnmounted(() => {
    clearAll();
    if (typeof window !== 'undefined') {
        window.removeEventListener('click', closeDropdowns);
        window.removeEventListener('scroll', closeDropdowns, { capture: true });
        window.removeEventListener('resize', closeDropdowns);
    }
});
</script>

<style scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Custom Scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #3b82f6 #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
  margin: 4px 0;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #3b82f6; /* Solid brand blue */
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #1d4ed8;
}

.inner-shadow-sm {
    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.02);
}

/* Dropdown Animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-out;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
